import { canonicalTimeZoneCase } from '@t4s/domain'

/**
 * The browser's zone names, and the one correction the console makes to a typed one.
 *
 * `docs/DECISIONS.md`, "Zones come from `Intl`": the options offered come from the browser's own
 * list. `Intl` *accepts* a name in any letter case, though, and so do `timeZoneSchema` and the API
 * — `europe/kyiv` was once created as `europe/kyiv` (P07 QA, observation 1). Since TAS-9 **the API
 * owns the canonical casing**: it puts a zone in its IANA letter case on every write
 * (`canonicalTimeZoneCase`, `@t4s/domain`), so a client that sends `europe/kyiv` gets `Europe/Kyiv`
 * stored. The console keeps correcting the case too, as a UX nicety rather than a dependency: the
 * field shows the canonical name the moment the owner leaves it, and the "Move business day"
 * confirmation and the request carry the same string the API will store (`canonicalTimeZone`).
 *
 * **A browser's list can lack a current name** (`fix-console-timezone-case`, P07 fix QA, D1).
 * `Intl.supportedValuesOf('timeZone')` follows CLDR's identifiers, which keep an old name for a
 * zone long after the IANA database renamed it: Chromium 141 and Node 24 list `Europe/Kiev`, not
 * `Europe/Kyiv`, and the browser still *accepts* `europe/kyiv`, resolving it to `Europe/Kiev`. So
 * the list alone cannot say how `europe/kyiv` is cased. `CURRENT_IANA_NAMES` is a short, fixed
 * supplement of the IANA names such a list omits, used for exactly two things and no more:
 * casing a typed name, and offering the name in the suggestions. See `docs/ux/PLATFORM-ADMIN.md`
 * §8.2.
 */

/**
 * The IANA names (tzdata 2026c, `zone.tab` and `zone1970.tab`) that the zone list of Chromium 141
 * and Node 24 does not carry, because CLDR still lists the zone under its former name. Found by
 * diffing those two files against `Intl.supportedValuesOf('timeZone')`; it is exactly these 19.
 *
 * **What this list is for:** adding the name to the suggestions. (It used to correct the case of a
 * typed name too; since TAS-9 that last step is `canonicalTimeZoneCase`, the one list the API and
 * the console now share, which holds these names and every other IANA name.) **What it is not
 * for:** it never replaces one name with another. `europe/kiev` stays `Europe/Kiev` and `asia/calcutta` stays
 * `Asia/Calcutta`; the owner chose that string and an alias is a different spelling of the same
 * zone, not an error. A name here is used only when the runtime itself accepts it, so the console
 * still cannot offer or fix up a value the validation would refuse.
 *
 * Add a name when a tzdata release creates or renames a zone and a supported browser's list lags;
 * a name the browser does list needs no entry (its own list wins).
 */
const CURRENT_IANA_NAMES: readonly string[] = [
  'Africa/Asmara',
  'America/Argentina/Buenos_Aires',
  'America/Argentina/Catamarca',
  'America/Argentina/Cordoba',
  'America/Argentina/Jujuy',
  'America/Argentina/Mendoza',
  'America/Atikokan',
  'America/Indiana/Indianapolis',
  'America/Kentucky/Louisville',
  'America/Nuuk',
  'Asia/Ho_Chi_Minh',
  'Asia/Kathmandu',
  'Asia/Kolkata',
  'Asia/Yangon',
  'Atlantic/Faroe',
  'Europe/Kyiv',
  'Pacific/Chuuk',
  'Pacific/Kanton',
  'Pacific/Pohnpei',
]

/** The runtime's own zone list, or `undefined` if it has none (an older browser). */
function supportedZones(): readonly string[] | undefined {
  const supported = (Intl as unknown as { supportedValuesOf?: (key: string) => string[] })
    .supportedValuesOf
  return typeof supported === 'function' ? supported('timeZone') : undefined
}

/** What is offered in a runtime with no list: short, and never empty. */
const FALLBACK_NAMES: readonly string[] = ['Europe/Berlin', 'Europe/Kyiv', 'Europe/London', 'UTC']

/** Whether `Intl` in this runtime accepts the name (in any case) as a time zone. */
function runtimeAccepts(zone: string): boolean {
  try {
    new Intl.DateTimeFormat('en', { timeZone: zone })
    return true
  } catch {
    return false
  }
}

/**
 * Every zone to offer: this browser's own list, plus the supplement's names that it lacks and that
 * it accepts — or, in a runtime with no list at all, the short fallback list unchanged.
 */
export function timeZoneNames(): readonly string[] {
  const browser = supportedZones()
  if (browser === undefined) {
    return FALLBACK_NAMES
  }
  const listed = new Set(browser.map((zone) => zone.toLowerCase()))
  const added = CURRENT_IANA_NAMES.filter(
    (zone) => !listed.has(zone.toLowerCase()) && runtimeAccepts(zone),
  )
  return added.length === 0 ? browser : [...browser, ...added].sort()
}

/**
 * The typed zone in the casing the browser lists it in, or the typed value untouched.
 *
 * **Only the letter case is corrected.** A name the browser does not list in any casing is returned
 * as typed (the form's validation, not this, says whether it is a zone), and an alias is never
 * swapped for another name: `asia/calcutta` is not turned into `Asia/Kolkata`, because that would
 * be a different string the owner did not type. In order:
 *
 * 1. the browser's own list, whose casing wins;
 * 2. a zone the runtime resolves to the same name in a different case (`UTC` is absent from
 *    Chromium's list, and `utc` resolves to it);
 * 3. the shared IANA list, `canonicalTimeZoneCase` (`europe/kyiv`, which the runtime accepts but
 *    resolves to `Europe/Kiev`, so step 2 cannot find it) — reached only when the runtime accepts
 *    the typed name, so a name no runtime knows is returned as typed and the form's validation
 *    refuses it. It is the same function the API applies on write, so for every name the
 *    browser's list does not carry the console and the API cannot disagree.
 */
export function canonicalTimeZone(typed: string): string {
  const lower = typed.toLowerCase()
  const listed = (supportedZones() ?? FALLBACK_NAMES).find((zone) => zone.toLowerCase() === lower)
  if (listed !== undefined) {
    return listed
  }
  try {
    const resolved = new Intl.DateTimeFormat('en', { timeZone: typed }).resolvedOptions().timeZone
    if (resolved.toLowerCase() === lower) {
      return resolved
    }
  } catch {
    // Not a zone at all: nothing to correct.
    return typed
  }
  return canonicalTimeZoneCase(typed)
}
